import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { resultsAPI, samplingPlansAPI, departmentsAPI, aiAPI, settingsAPI, deviationsAPI } from '../services/api';
import AIResultPanel from '../components/AIResultPanel';
import useAIAction from '../hooks/useAIAction';
import { Modal } from '../components/Modals';
import VersionHistoryModal from '../components/VersionHistoryModal';
import { FaRobot, FaPlus, FaSearch, FaPaperclip, FaTimes, FaFlask, FaVial, FaCheck, FaFileAlt, FaHistory } from 'react-icons/fa';
import FilterStatCards, { matchesGroup } from '../components/FilterStatCards';
import TablePagination from '../components/TablePagination';

const readFileAsDataUrl = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => resolve('');
  reader.readAsDataURL(file);
});

const getRows = (payload) => {
  if (Array.isArray(payload?.data?.data?.rows)) return payload.data.data.rows;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

const createEmptyDraft = () => ({
  plan_id: '',
  location_id: '',
  criterion_limit_id: '',
  batch_number: '',
  ar_no: '',
  entered_department: '',
  sample_date: '',
  criterion_label: '',
  sample_medium: '',
  result_type: '',
  acceptance_criteria_ref: '',
  limit_value: '',
  comments: '',
  attachment_name: '',
  attachment_data: ''
});

// Who may sign each step, and how to label the button/badge.
const CLEANING_DEPARTMENTS = ['Production'];
const SAMPLING_DEPARTMENTS = ['Production', 'QC-Chemical', 'QC-Micro', 'IPQA'];
const VISUAL_DEPARTMENTS = ['Production', 'IPQA'];

const resolveAnalysisDepartment = (row) => {
  const medium = String(row.result_type || row.sample_medium || row.criterion_label || '').toLowerCase();
  return medium.includes('micro') ? 'QC-Micro' : 'QC-Chemical';
};

const STEP_CONFIG = {
  draft: { type: 'cleaning', label: 'Sign Cleaning', departments: CLEANING_DEPARTMENTS },
  cleaned: { type: 'visual', label: 'Sign Visual Inspection', departments: VISUAL_DEPARTMENTS },
  visual_done: { type: 'sampling', label: 'Sign Sampling', departments: SAMPLING_DEPARTMENTS },
  sampled: { type: 'analyze', label: 'Analyze', departments: null }, // resolved per-row
  analyzed: { type: 'report', label: 'Report / Review', departments: null } // resolved per-row
};

const nextStepForRow = (row) => {
  const config = STEP_CONFIG[row.workflow_stage || 'draft'];
  if (!config) return null;
  const departments = config.departments || [resolveAnalysisDepartment(row)];
  return { ...config, departments };
};

// Clamps a numeric result/limit to a fixed decimal count so scientific notation and
// long floating-point tails never leak into an analytical or deviation record; leaves
// non-numeric values (blank, pass/fail text) untouched.
const fmtNum = (value, digits = 6) => {
  if (value === '' || value === null || value === undefined) return '-';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(digits);
};

// NMT (not-more-than) rule — every acceptance limit in this app (MAR/ppm/TOC/bioburden/
// conductivity) is an upper bound, so a numeric result <= the planned limit is a PASS.
// Mirrors the authoritative computation in backend/routes/results.js PUT /:id/analyze.
const computeAutoStatus = (resultValue, limitValue) => {
  const value = Number(resultValue);
  const limit = Number(limitValue);
  if (resultValue === '' || resultValue === null || resultValue === undefined || Number.isNaN(value)) return '';
  if (limitValue === '' || limitValue === null || limitValue === undefined || Number.isNaN(limit)) return '';
  return value <= limit ? 'PASS' : 'FAIL';
};

// One row of the Planned Acceptance Coverage table — shared by the flat and
// equipment-grouped render paths so the two stay in sync. Status here is entirely
// automatic: it's driven off the plan's approval status and the auto-created result's
// workflow_stage, never a manually-picked value.
const CoverageRow = ({ row, showEquipment, resultByPlanRowKey, planRowKey, startResultForRow }) => {
  const existing = resultByPlanRowKey.get(planRowKey(row.plan_id, row.location_id, row.criterion_limit_id));
  const planApproved = row.plan_status === 'Approved';
  return (
    <tr>
      {showEquipment && <td>{row.equipment_name || '-'}</td>}
      <td>{row.plan_code}</td>
      <td>{row.location_code || '-'} - {row.location_name}</td>
      <td>{row.sample_id || '-'}</td>
      <td>{row.sample_type}</td>
      <td>{row.criterion_label}</td>
      <td>{row.limit_value ? fmtNum(row.limit_value) : '-'} {row.limit_unit || ''}</td>
      <td>{row.acceptance_criteria_ref || '-'}</td>
      <td>
        {existing
          ? <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>{STAGE_LABELS[existing.workflow_stage || 'draft'] || existing.workflow_stage}</span>
          : planApproved
            ? <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 700, color: 'var(--warning-color)' }}>Sampling Plan Ready</span>
            : <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-secondary)' }}>Plan in Draft</span>}
      </td>
      <td>
        {existing ? (
          <span style={{ color: 'var(--success-color)', fontSize: 'var(--fs-sm)' }}>In sign-off trail below</span>
        ) : planApproved ? (
          <button className="btn btn-primary btn-sm" type="button" onClick={() => startResultForRow(row)}>Enter Result</button>
        ) : (
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }} title="Results are auto-created once this Sampling Plan is QA Approved">Awaiting plan approval</span>
        )}
      </td>
    </tr>
  );
};

// Every one of these is set automatically off workflow_stage — nothing here is a
// manually-picked status field. "Under Sampling" covers cleaning through visual
// inspection; "Under Analysis" covers lab analysis through report/review.
const STAGE_LABELS = {
  draft: 'Under Sampling — awaiting cleaning sign-off',
  cleaned: 'Under Sampling — awaiting visual inspection',
  visual_done: 'Under Sampling — awaiting sample collection',
  sampled: 'Under Analysis — awaiting lab analysis',
  analyzed: 'Under Analysis — awaiting report/review',
  reported: 'Reported',
  approved: 'Approved'
};

const stageOf = (r) => r.workflow_stage || 'draft';
const RESULT_GROUPS = [
  { key: 'sampling', title: 'Under Sampling', icon: FaVial, color: 'warning', match: (r) => ['draft', 'cleaned', 'sampled'].includes(stageOf(r)) },
  { key: 'analysis', title: 'Under Analysis', icon: FaFlask, color: 'info', match: (r) => ['visual_done', 'analyzed'].includes(stageOf(r)) },
  { key: 'reported', title: 'Reported', icon: FaFileAlt, color: 'primary', match: (r) => stageOf(r) === 'reported' },
  { key: 'approved', title: 'Approved', icon: FaCheck, color: 'success', match: (r) => stageOf(r) === 'approved' },
];

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [plans, setPlans] = useState([]);
  const [results, setResults] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [draft, setDraft] = useState(createEmptyDraft());
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [historyModal, setHistoryModal] = useState(null); // result row
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [coverageSearch, setCoverageSearch] = useState('');
  const [coverageGrouped, setCoverageGrouped] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const [resultsSearch, setResultsSearch] = useState('');
  const [resultsStageFilter, setResultsStageFilter] = useState('');
  const [resultsCard, setResultsCard] = useState('All');
  const [sortKey, setSortKey] = useState('entered_at');
  const [sortDir, setSortDir] = useState('desc');
  const [action, setAction] = useState(null); // { type, row, fields }
  const [samplingIntimationEnabled, setSamplingIntimationEnabled] = useState(true);
  const [deviations, setDeviations] = useState([]);
  const [deviationModal, setDeviationModal] = useState(null);
  const ai = useAIAction();
  const currentUser = getCurrentUser();
  const currentDepartment = String(currentUser.department || '').trim();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [plansRes, resultsRes, deptRes, settingsRes, deviationsRes] = await Promise.all([
          samplingPlansAPI.getAll(),
          resultsAPI.getAll(),
          departmentsAPI.getAll(),
          settingsAPI.getAll().catch(() => null),
          deviationsAPI.getAll().catch(() => null)
        ]);
        setPlans(getRows(plansRes));
        setResults(getRows(resultsRes));
        setDepartments(getRows(deptRes));
        setSamplingIntimationEnabled(settingsRes?.data?.data?.sampling_intimation_enabled !== 'false');
        setDeviations(getRows(deviationsRes));
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load results data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const refreshResults = async () => {
    const refreshed = await resultsAPI.getAll();
    setResults(getRows(refreshed));
  };

  const refreshDeviations = async () => {
    const refreshed = await deviationsAPI.getAll();
    setDeviations(getRows(refreshed));
  };

  const openDeviationModal = (result) => {
    const candidates = deviations.filter((d) => String(d.result_id) === String(result.result_id));
    const deviation = candidates.find((d) => d.status === 'Open') || candidates[0];
    if (!deviation) { toast.info('No deviation record found for this result'); return; }
    setDeviationModal({ deviation, result, cause: deviation.cause || '', investigation_comment: deviation.investigation_comment || '', disposition: '', disposition_comment: '', password: '' });
  };

  const saveDeviationInvestigation = async () => {
    if (!deviationModal) return;
    try {
      await deviationsAPI.update(deviationModal.deviation.deviation_id, {
        cause: deviationModal.cause, investigation_comment: deviationModal.investigation_comment
      });
      toast.success('Investigation notes saved');
      await refreshDeviations();
      setDeviationModal(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save investigation notes');
    }
  };

  const closeDeviation = async () => {
    if (!deviationModal) return;
    if (!deviationModal.disposition) return toast.warning('Select a disposition');
    if (!deviationModal.disposition_comment.trim()) return toast.warning('Disposition rationale is required');
    if (!deviationModal.password) return toast.warning('Password is required to close a deviation');
    try {
      await deviationsAPI.update(deviationModal.deviation.deviation_id, {
        cause: deviationModal.cause, investigation_comment: deviationModal.investigation_comment,
        disposition: deviationModal.disposition, disposition_comment: deviationModal.disposition_comment,
        close: true, password: deviationModal.password
      });
      toast.success('Deviation closed');
      await refreshDeviations();
      setDeviationModal(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to close deviation');
    }
  };

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.plan_id) === String(draft.plan_id)) || null,
    [plans, draft.plan_id]
  );

  const selectedLocation = useMemo(
    () => (selectedPlan?.locations || []).find((location) => String(location.location_id) === String(draft.location_id)) || null,
    [selectedPlan, draft.location_id]
  );

  const planRowKey = (planId, locationId, criterionLimitId) => `${planId}-${locationId}-${criterionLimitId}`;

  // Every sample defined across every saved Sampling Plan — this IS the full list of
  // things that need a result. Cross-referenced against existing results below so the
  // list shows what's still pending vs already started, instead of everyone having to
  // re-pick Plan/Location/Criterion by hand even though it's all sitting right here.
  const flattenedPlanRows = useMemo(
    () => plans.flatMap((plan) =>
      (plan.locations || []).flatMap((location) =>
        (location.acceptance_criteria || []).map((criterion) => ({
          plan_id: plan.plan_id,
          plan_code: plan.plan_code,
          plan_status: plan.status || 'Draft',
          equipment_name: plan.equipment_name || 'Unassigned Equipment',
          train_name: plan.train_name || '',
          location_id: location.location_id,
          location_name: location.location_name,
          location_code: location.location_code,
          sample_type: location.sample_type,
          sample_id: location.metadata?.sample_id || '',
          criterion_limit_id: criterion.limit_id,
          criterion_label: criterion.criterion_label,
          acceptance_criteria_ref: criterion.acceptance_criteria_ref || location.acceptance_criteria_ref,
          limit_value: criterion.limit_value,
          limit_unit: criterion.limit_unit
        }))
      )
    ),
    [plans]
  );

  const resultByPlanRowKey = useMemo(() => {
    const map = new Map();
    results.forEach((result) => {
      map.set(planRowKey(result.plan_id, result.location_id, result.criterion_limit_id), result);
    });
    return map;
  }, [results]);

  // ── Planned Acceptance Coverage: free-text search over plan/location/sample/criterion ──
  const filteredCoverageRows = useMemo(() => {
    const q = coverageSearch.trim().toLowerCase();
    if (!q) return flattenedPlanRows;
    return flattenedPlanRows.filter((row) =>
      (row.plan_code || '').toLowerCase().includes(q) ||
      (row.equipment_name || '').toLowerCase().includes(q) ||
      (row.location_name || '').toLowerCase().includes(q) ||
      (row.location_code || '').toLowerCase().includes(q) ||
      (row.sample_id || '').toLowerCase().includes(q) ||
      (row.sample_type || '').toLowerCase().includes(q) ||
      (row.criterion_label || '').toLowerCase().includes(q) ||
      (row.acceptance_criteria_ref || '').toLowerCase().includes(q)
    );
  }, [flattenedPlanRows, coverageSearch]);

  // Equipment-wise grouping for the coverage table — one collapsible section per equipment,
  // in first-seen order, so operators can fold away trains they're not currently sampling.
  const coverageGroups = useMemo(() => {
    const order = [];
    const map = new Map();
    filteredCoverageRows.forEach((row) => {
      const key = row.equipment_name || 'Unassigned Equipment';
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(row);
    });
    return order.map((key) => ({ key, rows: map.get(key) }));
  }, [filteredCoverageRows]);

  const toggleGroupCollapsed = (key) => setCollapsedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // ── Entered Results: stage filter + free-text search + sort ──
  const filteredResults = useMemo(() => {
    let rows = resultsStageFilter ? results.filter((r) => (r.workflow_stage || 'draft') === resultsStageFilter) : results;
    rows = rows.filter((r) => matchesGroup(RESULT_GROUPS, resultsCard, r));
    const q = resultsSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        (r.plan_code || '').toLowerCase().includes(q) ||
        (r.location_name || '').toLowerCase().includes(q) ||
        (r.location_code || '').toLowerCase().includes(q) ||
        (r.batch_number || '').toLowerCase().includes(q) ||
        (r.ar_no || '').toLowerCase().includes(q) ||
        (r.criterion_label || r.planned_criterion_label || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...rows].sort((a, b) => {
      let aVal, bVal;
      if (sortKey === 'batch_number') { aVal = a.batch_number || ''; bVal = b.batch_number || ''; }
      else if (sortKey === 'stage') { aVal = a.workflow_stage || 'draft'; bVal = b.workflow_stage || 'draft'; }
      else { aVal = a.entered_at ? new Date(a.entered_at).getTime() : 0; bVal = b.entered_at ? new Date(b.entered_at).getTime() : 0; }
      if (typeof aVal === 'number' && typeof bVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [results, resultsStageFilter, resultsCard, resultsSearch, sortKey, sortDir]);

  const [resultsPage, setResultsPage] = useState(1);
  const [resultsPageSize, setResultsPageSize] = useState(25);
  const resultsTotalPages = Math.max(1, Math.ceil(filteredResults.length / resultsPageSize));
  useEffect(() => { setResultsPage(1); }, [resultsStageFilter, resultsCard, resultsSearch, resultsPageSize]);
  useEffect(() => { if (resultsPage > resultsTotalPages) setResultsPage(resultsTotalPages); }, [resultsPage, resultsTotalPages]);
  const paginatedResults = filteredResults.slice((resultsPage - 1) * resultsPageSize, resultsPage * resultsPageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  const sortIndicator = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  const handleDraftField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handleAttachmentChange = async (file) => {
    if (!file) return;
    setAttachmentUploading(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setDraft((prev) => ({ ...prev, attachment_name: file.name, attachment_data: dataUrl }));
    } finally {
      setAttachmentUploading(false);
    }
  };

  const openNewDraftModal = () => {
    setDraft(createEmptyDraft());
    setDraftModalOpen(true);
  };

  const startResultForRow = (row) => {
    setDraft({
      ...createEmptyDraft(),
      plan_id: String(row.plan_id),
      location_id: String(row.location_id),
      criterion_limit_id: String(row.criterion_limit_id),
      criterion_label: row.criterion_label || '',
      sample_medium: row.sample_type || '',
      result_type: row.sample_type || '',
      acceptance_criteria_ref: row.acceptance_criteria_ref || '',
      limit_value: row.limit_value ?? ''
    });
    setDraftModalOpen(true);
  };

  const handlePlanChange = (planId) => {
    setDraft((prev) => ({ ...createEmptyDraft(), entered_department: prev.entered_department, plan_id: planId }));
  };

  const handleLocationChange = (locationId) => {
    const location = (selectedPlan?.locations || []).find((item) => String(item.location_id) === String(locationId));
    setDraft((prev) => ({
      ...prev,
      location_id: locationId,
      criterion_limit_id: '',
      sample_medium: location?.sample_type || '',
      result_type: location?.sample_type || ''
    }));
  };

  const handleCriterionChange = (criterionId) => {
    const criterion = (selectedLocation?.acceptance_criteria || []).find((item) => String(item.limit_id) === String(criterionId));
    setDraft((prev) => ({
      ...prev,
      criterion_limit_id: criterionId,
      criterion_label: criterion?.criterion_label || '',
      sample_medium: criterion?.sample_medium || selectedLocation?.sample_type || '',
      result_type: criterion?.limit_type || selectedLocation?.sample_type || '',
      limit_value: criterion?.limit_value ?? '',
      acceptance_criteria_ref: criterion?.acceptance_criteria_ref || selectedLocation?.acceptance_criteria_ref || ''
    }));
  };

  const saveDraft = async () => {
    if (!draft.plan_id || !draft.location_id) {
      toast.error('Plan and location are required');
      return;
    }

    try {
      setSaving(true);
      await resultsAPI.create(draft);
      toast.success('Draft result created — sign off each step below');
      await refreshResults();
      setDraft(createEmptyDraft());
      setDraftModalOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save result');
    } finally {
      setSaving(false);
    }
  };

  const openAction = (row) => {
    const step = nextStepForRow(row);
    if (!step) return;
    setAction({
      type: step.type,
      row,
      departments: step.departments,
      esign_password: '',
      esign_comment: '',
      visual_inspection_status: '',
      visual_inspection_comments: '',
      result_value: '',
      observed_value: row.observed_value || '',
      result_unit: row.result_unit || '',
      result_status: row.result_status || '',
      result_status_touched: false
    });
  };

  const openIntimationAction = (row, intimationAction) => {
    const departments = intimationAction === 'send'
      ? SAMPLING_DEPARTMENTS
      : ['QC-Chemical', 'QC-Micro', 'Production', 'IPQA'];
    setAction({
      type: 'intimation',
      intimationAction,
      row,
      departments,
      esign_password: '',
      esign_comment: ''
    });
  };

  useEffect(() => {
    if (!location.state?.openViewResultId || results.length === 0 || action) return;
    const requestedResultId = String(location.state.openViewResultId);
    const selectedResult = results.find((r) => String(r.result_id) === requestedResultId);
    if (selectedResult) {
      openAction(selectedResult);
    } else {
      toast.info('That result is no longer pending at this step');
    }
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, results]);

  // Carries the workflow stage a dashboard chart was drilled into (e.g. the
  // "Sample & Test Workflow" funnel bar) through to this list's own stage filter.
  useEffect(() => {
    if (!location.state?.filterStage) return;
    setResultsStageFilter(location.state.filterStage);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const closeAction = () => setAction(null);

  const submitAction = async () => {
    if (!action) return;
    if (!action.esign_password) {
      toast.error('Electronic signature password is required');
      return;
    }
    if (action.type === 'visual' && !action.visual_inspection_status) {
      toast.error('Select a visual inspection status');
      return;
    }

    try {
      setActing(true);
      const id = action.row.result_id;
      let response;
      if (action.type === 'cleaning') {
        response = await resultsAPI.signCleaning(id, { esign_password: action.esign_password, esign_comment: action.esign_comment });
      } else if (action.type === 'sampling') {
        response = await resultsAPI.signSampling(id, { esign_password: action.esign_password, esign_comment: action.esign_comment });
      } else if (action.type === 'intimation') {
        response = await resultsAPI.samplingIntimation(id, {
          action: action.intimationAction,
          esign_password: action.esign_password,
          esign_comment: action.esign_comment
        });
      } else if (action.type === 'visual') {
        response = await resultsAPI.signVisual(id, {
          esign_password: action.esign_password,
          esign_comment: action.esign_comment,
          visual_inspection_status: action.visual_inspection_status,
          visual_inspection_comments: action.visual_inspection_comments
        });
      } else if (action.type === 'analyze') {
        response = await resultsAPI.analyze(id, {
          esign_password: action.esign_password,
          esign_comment: action.esign_comment,
          result_value: action.result_value,
          observed_value: action.observed_value,
          result_unit: action.result_unit,
          // Only send result_status when the analyst explicitly touched the dropdown —
          // otherwise leave it out so the backend performs the authoritative auto-compute
          // against the canonical limit_value on the row, rather than trusting this
          // live client-side preview (which could drift if limit_value differs).
          result_status: action.result_status_touched ? action.result_status : undefined
        });
      } else if (action.type === 'report') {
        response = await resultsAPI.approve(id, { esign_password: action.esign_password, comments: action.esign_comment });
      }

      const trainingWarning = response?.data?.training_warning;
      if (trainingWarning) {
        toast.warn(`Signed — ${trainingWarning}`);
      } else {
        toast.success(action.type === 'intimation' ? 'Sampling intimation record updated' : 'Signed successfully');
      }
      await refreshResults();
      if (action.type === 'analyze' && response?.data?.deviation) await refreshDeviations();
      closeAction();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to sign this step');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 className="page-title">Results And Sampling Sign-Off Workflow</h2>
        <p className="page-subtitle">
          Every result moves through a sequential, department-gated electronic signature trail:
          Cleaning (Production) → Visual Inspection (Production / IPQA) → Sampling → Analysis
          (QC-Chemical / QC-Micro) → Report / Review. Your department: <strong>{currentDepartment || 'not set'}</strong>.
        </p>
      </div>

      <FilterStatCards groups={RESULT_GROUPS} records={results} value={resultsCard} onChange={setResultsCard} totalTitle="Total Results" totalIcon={FaFileAlt} />

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Planned Acceptance Coverage</h2>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
              Every sample defined across all Sampling Plans, with its acceptance criterion and limit. Results are created automatically once a plan is QA Approved — "Enter Result" is only needed for an ad-hoc sample not yet covered.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
              <input className="form-control form-control-sm" style={{ paddingLeft: '30px', width: '220px' }} placeholder="Search plan, equipment, criterion..." value={coverageSearch} onChange={(e) => setCoverageSearch(e.target.value)} />
            </div>
            <button
              className={`btn btn-sm ${coverageGrouped ? 'btn-primary' : 'btn-outline'}`}
              type="button"
              onClick={() => setCoverageGrouped((v) => !v)}
              title="Group rows by equipment"
            >
              Group by Equipment
            </button>
            <button className="btn btn-primary btn-sm" type="button" onClick={openNewDraftModal}><FaPlus /> New Draft Result</button>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {!coverageGrouped && <th>Equipment</th>}
                <th>Plan</th>
                <th>Location</th>
                <th>Sample ID</th>
                <th>Sample Type</th>
                <th>Criterion</th>
                <th>Limit</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoverageRows.length === 0 ? (
                <tr><td colSpan={coverageGrouped ? 9 : 10} className="text-center text-muted">No planned criteria found</td></tr>
              ) : coverageGrouped ? coverageGroups.map((group) => {
                const isCollapsed = collapsedGroups.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    <tr onClick={() => toggleGroupCollapsed(group.key)} style={{ cursor: 'pointer', background: 'var(--surface-light)' }}>
                      <td colSpan={9} style={{ fontWeight: 700, fontSize: 'var(--fs-md)' }}>
                        <span style={{ display: 'inline-block', width: '14px', color: 'var(--text-secondary)' }}>{isCollapsed ? '▸' : '▾'}</span>
                        {group.key}
                        <span style={{ marginLeft: '8px', fontWeight: 500, fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>({group.rows.length} sample{group.rows.length === 1 ? '' : 's'})</span>
                      </td>
                    </tr>
                    {!isCollapsed && group.rows.map((row) => (
                      <CoverageRow key={planRowKey(row.plan_id, row.location_id, row.criterion_limit_id)} row={row} showEquipment={false} resultByPlanRowKey={resultByPlanRowKey} planRowKey={planRowKey} startResultForRow={startResultForRow} />
                    ))}
                  </React.Fragment>
                );
              }) : filteredCoverageRows.map((row) => (
                <CoverageRow key={planRowKey(row.plan_id, row.location_id, row.criterion_limit_id)} row={row} showEquipment resultByPlanRowKey={resultByPlanRowKey} planRowKey={planRowKey} startResultForRow={startResultForRow} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>Entered Results — Sign-Off Trail</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }} />
              <input className="form-control form-control-sm" style={{ paddingLeft: '30px', width: '190px' }} placeholder="Search batch, AR no., plan..." value={resultsSearch} onChange={(e) => setResultsSearch(e.target.value)} />
            </div>
            <select className="form-control form-control-sm" style={{ width: '200px' }} value={resultsStageFilter} onChange={(e) => setResultsStageFilter(e.target.value)}>
              <option value="">All stages</option>
              {Object.entries(STAGE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
            <button className="btn btn-outline btn-sm" type="button" data-module="ai_assistant" onClick={() => ai.run('AI Draft Report — Results Summary', () => aiAPI.draftReport({ reportType: 'results' }), 'report')}>
              <FaRobot /> AI Draft Report
            </button>
          </div>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Plan / Location</th>
                <th>Criterion</th>
                <th>Acceptance Criteria</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('batch_number')}>Batch / AR No.{sortIndicator('batch_number')}</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('stage')}>Stage{sortIndicator('stage')}</th>
                <th>Cleaned</th>
                <th>Sampled</th>
                <th>Visual</th>
                <th>Analyzed</th>
                <th>Reported</th>
                <th>Raw Data</th>
                <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('entered_at')}>Next Action{sortIndicator('entered_at')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.length === 0 ? (
                <tr><td colSpan={12} className="text-center text-muted">No data available</td></tr>
              ) : paginatedResults.map((result) => {
                const step = nextStepForRow(result);
                const eligible = step ? step.departments.some((d) => d.toLowerCase() === currentDepartment.toLowerCase()) : false;
                const intimationAvailable = samplingIntimationEnabled && ['cleaned', 'sampled', 'visual_done', 'analyzed'].includes(result.workflow_stage || 'draft');
                return (
                  <tr key={result.result_id}>
                    <td>
                      {result.plan_id ? (
                        <Link to={`/study-report/${result.plan_id}`} className="record-link" title="View study report / plan">{result.plan_code}</Link>
                      ) : result.plan_code}
                      <br /><span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>{result.location_code || '-'} - {result.location_name}</span>{result.sampling_area_grade ? <><br /><span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-xs)' }}>Sampling area: {result.sampling_area_grade}</span></> : null}
                    </td>
                    <td>{result.criterion_label || result.planned_criterion_label || result.result_type}</td>
                    <td>
                      <span style={{ fontSize: 'var(--fs-sm)' }}>{result.acceptance_criteria_ref || result.planned_acceptance_criteria_ref || '-'}</span>
                      <br />
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                        Limit: {fmtNum(result.limit_value ?? result.planned_limit_value)} {result.result_unit || result.planned_limit_unit || ''}
                      </span>
                    </td>
                    <td>{result.batch_number || '-'} / {result.ar_no || '-'}</td>
                    <td>{STAGE_LABELS[result.workflow_stage || 'draft'] || result.workflow_stage}</td>
                    <td>{result.cleaned_by_name ? <span title={result.cleaned_at}>{result.cleaned_by_name}<br />({result.cleaned_by_department})</span> : '-'}</td>
                    <td>
                      {result.sampled_by_name ? <span title={result.sampled_at}>{result.sampled_by_name}<br />({result.sampled_by_department})</span> : '-'}
                      {intimationAvailable ? (
                        <div style={{ marginTop: '4px' }}>
                          {result.sampling_intimation_sent_at ? <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Intimation recorded</span> : null}
                          <button className="btn btn-outline btn-sm" type="button" onClick={() => openIntimationAction(result, 'send')} disabled={!!result.sampling_intimation_sent_at || !SAMPLING_DEPARTMENTS.some((d) => d.toLowerCase() === currentDepartment.toLowerCase())}>{result.sampling_intimation_sent_at ? 'Intimation Sent' : 'Record Intimation'}</button>
                        </div>
                      ) : null}
                      {result.sampling_intimation_sent_at ? (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{result.sampling_intimation_acknowledged ? `Acknowledged by ${result.sampling_intimation_ack_by || 'team'}` : 'Awaiting acknowledgement'}</span>
                          {!result.sampling_intimation_acknowledged && <button className="btn btn-outline btn-sm" type="button" onClick={() => openIntimationAction(result, 'acknowledge')} disabled={!['QC-Chemical', 'QC-Micro', 'Production', 'IPQA'].some((d) => d.toLowerCase() === currentDepartment.toLowerCase())}>Acknowledge</button>}
                        </div>
                      ) : null}
                    </td>
                    <td>{result.visual_inspection_by_name ? <span title={result.visual_inspection_at}>{result.visual_inspection_status}<br />{result.visual_inspection_by_name} ({result.visual_by_department})</span> : '-'}</td>
                    <td>{result.analyzed_by_name ? <span title={result.analyzed_at}>{result.result_value != null ? fmtNum(result.result_value) : (result.observed_value ?? '-')}{result.result_unit ? ` ${result.result_unit}` : ''} {result.result_status ? <strong>({result.result_status})</strong> : null}{result.result_status_source ? <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}> {result.result_status_source === 'auto' ? '(auto)' : '(overridden)'}</span> : null}<br />{result.analyzed_by_name} ({result.analyzed_by_department})</span> : '-'}
                      {result.result_status === 'FAIL' && (() => {
                        const openDev = deviations.find((d) => String(d.result_id) === String(result.result_id) && d.status === 'Open');
                        return (
                          <div style={{ marginTop: '4px' }}>
                            <button className="btn btn-outline btn-sm" type="button" onClick={() => openDeviationModal(result)} style={{ color: openDev ? 'var(--danger-text)' : undefined, borderColor: openDev ? 'var(--danger-border)' : undefined }}>
                              {openDev ? 'Deviation: Open' : 'Deviation: Closed'}
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                    <td>{result.reported_by_name ? <span title={result.reported_at}>{result.reported_by_name}<br />({result.reported_by_department})</span> : '-'}</td>
                    <td>
                      {result.attachment_name ? (
                        <a href={result.attachment_data} download={result.attachment_name} title={result.attachment_name} style={{ fontSize: 'var(--fs-sm)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <FaPaperclip /> {result.attachment_name.length > 14 ? `${result.attachment_name.slice(0, 12)}…` : result.attachment_name}
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      {step ? (
                        <button
                          className="btn btn-primary btn-sm"
                          type="button"
                          onClick={() => openAction(result)}
                          disabled={!eligible}
                          title={eligible ? '' : `Requires ${step.departments.join(' or ')} department`}
                        >
                          {step.label}
                        </button>
                      ) : (
                        <span style={{ color: 'var(--success-color)', fontWeight: 600 }}>Complete</span>
                      )}
                      {result.status === 'QA Approved' && (
                        <div style={{ marginTop: '4px' }}>
                          <button className="btn btn-outline btn-sm" type="button" title="Version history" aria-label="Version history" onClick={() => setHistoryModal(result)}>
                            <FaHistory />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredResults.length > 0 && (
          <TablePagination total={filteredResults.length} page={resultsPage} pageSize={resultsPageSize} onPageChange={setResultsPage} onPageSizeChange={(n) => { setResultsPageSize(n); setResultsPage(1); }} itemLabel="results" />
        )}
      </div>

      <Modal
        isOpen={draftModalOpen}
        onClose={() => setDraftModalOpen(false)}
        title="New Draft Result"
        size="lg"
        footer={
          <>
            <button className="btn btn-outline" onClick={() => setDraftModalOpen(false)} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={saveDraft} disabled={saving}>{saving ? 'Saving...' : 'Create Draft Result'}</button>
          </>
        }
      >
        <div className="modal-section">
          <h3 className="modal-section-title">Sample Details</h3>
          <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Plan</label>
            <select className="form-control" value={draft.plan_id} onChange={(e) => handlePlanChange(e.target.value)}>
              <option value="">Select plan</option>
              {plans.map((plan) => (
                <option key={plan.plan_id} value={plan.plan_id}>{plan.plan_code} - {plan.train_name || ''}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Location</label>
            <select className="form-control" value={draft.location_id} onChange={(e) => handleLocationChange(e.target.value)} disabled={!selectedPlan}>
              <option value="">Select location</option>
              {(selectedPlan?.locations || []).map((location) => (
                <option key={location.location_id} value={location.location_id}>
                  {location.location_code || '-'} - {location.location_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Acceptance Criterion</label>
            <select className="form-control" value={draft.criterion_limit_id} onChange={(e) => handleCriterionChange(e.target.value)} disabled={!selectedLocation}>
              <option value="">Select criterion</option>
              {(selectedLocation?.acceptance_criteria || []).map((criterion) => (
                <option key={criterion.limit_id} value={criterion.limit_id}>
                  {criterion.criterion_label} ({criterion.limit_value ? fmtNum(criterion.limit_value) : '-'} {criterion.limit_unit || ''})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">AR No.</label>
            <input className="form-control" value={draft.ar_no} onChange={(e) => handleDraftField('ar_no', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Batch Number</label>
            <input className="form-control" value={draft.batch_number} onChange={(e) => handleDraftField('batch_number', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Department (context)</label>
            <select className="form-control" value={draft.entered_department} onChange={(e) => handleDraftField('entered_department', e.target.value)}>
              <option value="">Select department</option>
              {departments.map((dept) => (
                <option key={dept.department_id} value={dept.department_name}>{dept.department_name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Sample Date (planned)</label>
            <input className="form-control" type="date" value={draft.sample_date} onChange={(e) => handleDraftField('sample_date', e.target.value)} />
          </div>
          </div>
        </div>

        <div className="modal-section">
          <h3 className="modal-section-title">Comments &amp; Attachment</h3>
          <div className="form-group">
            <label className="form-label">Comments</label>
            <textarea className="form-control" rows={2} value={draft.comments} onChange={(e) => handleDraftField('comments', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Raw Data Attachment</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="file" onChange={(e) => handleAttachmentChange(e.target.files?.[0])} disabled={attachmentUploading} />
              {draft.attachment_name && (
                <span style={{ fontSize: 'var(--fs-sm)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <FaPaperclip /> {draft.attachment_name}
                  <button type="button" className="btn btn-sm btn-danger-outline" onClick={() => setDraft((p) => ({ ...p, attachment_name: '', attachment_data: '' }))}><FaTimes /></button>
                </span>
              )}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>Attach the instrument printout / raw data file backing this sample (balance, meter, chromatogram, etc.).</p>
          </div>
        </div>
      </Modal>

      {action && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '480px', maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <h2 className="card-title" style={{ fontSize: 'var(--fs-lg)' }}>
                {action.type === 'intimation' ? (action.intimationAction === 'send' ? 'Record Sampling Intimation' : 'Acknowledge Sampling Intimation') : (STEP_CONFIG[action.row.workflow_stage || 'draft']?.label || 'Sign')}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>
                Required department: {action.departments.join(' or ')}. Signing as {currentUser.employee_name || currentUser.username} ({currentDepartment || 'no department set'}).
              </p>
            </div>
            <div style={{ padding: '16px' }}>
              {action.type === 'visual' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Visual Inspection Status</label>
                    <select className="form-control" value={action.visual_inspection_status} onChange={(e) => setAction((prev) => ({ ...prev, visual_inspection_status: e.target.value }))}>
                      <option value="">Select status</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                      <option value="NOT_APPLICABLE">Not Applicable</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Visual Inspection Comments</label>
                    <textarea className="form-control" rows={2} value={action.visual_inspection_comments} onChange={(e) => setAction((prev) => ({ ...prev, visual_inspection_comments: e.target.value }))} />
                  </div>
                </>
              )}

              {action.type === 'analyze' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Result Value</label>
                    <input
                      className="form-control"
                      type="number"
                      value={action.result_value}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAction((prev) => {
                          if (prev.result_status_touched) return { ...prev, result_value: value };
                          const auto = computeAutoStatus(value, action.row.limit_value);
                          return { ...prev, result_value: value, result_status: auto || prev.result_status };
                        });
                      }}
                    />
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                      Planned limit: {fmtNum(action.row.limit_value)} {action.row.limit_unit || action.row.planned_limit_unit || ''} (NMT — result must be ≤ limit to PASS)
                    </p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Observed Value / Narrative</label>
                    <textarea className="form-control" rows={2} value={action.observed_value} onChange={(e) => setAction((prev) => ({ ...prev, observed_value: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Result Unit</label>
                    <input className="form-control" value={action.result_unit} onChange={(e) => setAction((prev) => ({ ...prev, result_unit: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      Result Status{' '}
                      {action.result_status && (
                        <span style={{
                          marginLeft: '6px', fontSize: 'var(--fs-xs)', fontWeight: 700, padding: '1px 8px', borderRadius: '999px',
                          background: action.result_status_touched ? 'var(--warning-bg)' : 'var(--info-bg)',
                          color: action.result_status_touched ? 'var(--warning-text)' : 'var(--info-text)',
                          border: `1px solid ${action.result_status_touched ? 'var(--warning-border)' : 'var(--info-border)'}`
                        }}>
                          {action.result_status_touched ? 'Overridden' : 'Auto-computed'}
                        </span>
                      )}
                    </label>
                    <select
                      className="form-control"
                      value={action.result_status}
                      onChange={(e) => setAction((prev) => ({ ...prev, result_status: e.target.value, result_status_touched: true }))}
                    >
                      <option value="">Select</option>
                      <option value="PASS">PASS</option>
                      <option value="FAIL">FAIL</option>
                    </select>
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="form-label">Electronic Signature Password</label>
                <input className="form-control" type="password" value={action.esign_password} onChange={(e) => setAction((prev) => ({ ...prev, esign_password: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Comment</label>
                <textarea className="form-control" rows={2} value={action.esign_comment} onChange={(e) => setAction((prev) => ({ ...prev, esign_comment: e.target.value }))} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <button className="btn btn-outline" type="button" onClick={closeAction} disabled={acting}>Cancel</button>
                <button className="btn btn-primary" type="button" onClick={submitAction} disabled={acting}>
                  {acting ? 'Signing...' : 'Sign & Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AIResultPanel
        isOpen={ai.isOpen}
        onClose={ai.close}
        title={ai.title}
        loading={ai.loading}
        error={ai.error}
        content={ai.content}
        docType="report"
        exportFileName="results-ai-report.pdf"
      />

      {deviationModal && (
        <Modal
          isOpen
          onClose={() => setDeviationModal(null)}
          title={`Deviation — AR ${deviationModal.result.ar_no || deviationModal.result.result_id}`}
          size="md"
          footer={
            deviationModal.deviation.status === 'Closed' ? (
              <button className="btn btn-outline" onClick={() => setDeviationModal(null)}>Close</button>
            ) : (
              <>
                <button className="btn btn-outline" onClick={() => setDeviationModal(null)}>Cancel</button>
                <button className="btn btn-outline" onClick={saveDeviationInvestigation}>Save Notes</button>
                <button className="btn btn-primary" onClick={closeDeviation}>Close Deviation</button>
              </>
            )
          }
        >
          <div className="form-group">
            <label className="form-label">Result</label>
            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              {deviationModal.result.criterion_label} — {fmtNum(deviationModal.result.result_value)} vs limit {fmtNum(deviationModal.result.limit_value)}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Cause</label>
            <textarea className="form-control" rows={2} value={deviationModal.cause}
              disabled={deviationModal.deviation.status === 'Closed'}
              onChange={(e) => setDeviationModal((prev) => ({ ...prev, cause: e.target.value }))}
              placeholder="Root cause of the failure, if known..." />
          </div>
          <div className="form-group">
            <label className="form-label">Investigation Notes</label>
            <textarea className="form-control" rows={3} value={deviationModal.investigation_comment}
              disabled={deviationModal.deviation.status === 'Closed'}
              onChange={(e) => setDeviationModal((prev) => ({ ...prev, investigation_comment: e.target.value }))}
              placeholder="Investigation findings..." />
          </div>
          {deviationModal.deviation.status === 'Closed' ? (
            <div className="form-group">
              <label className="form-label">Disposition</label>
              <div style={{ fontSize: 'var(--fs-sm)' }}>
                <strong>{deviationModal.deviation.disposition}</strong> — {deviationModal.deviation.disposition_comment}
                <div style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>Closed by {deviationModal.deviation.qa_disposition_by_name} on {new Date(deviationModal.deviation.qa_disposition_at).toLocaleString()}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Disposition (required to close)</label>
                <select className="form-control" value={deviationModal.disposition} onChange={(e) => setDeviationModal((prev) => ({ ...prev, disposition: e.target.value }))}>
                  <option value="">Select disposition</option>
                  <option value="Retest">Retest</option>
                  <option value="Resample">Resample</option>
                  <option value="Accepted As-Is">Accepted As-Is</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Disposition Rationale (required to close)</label>
                <textarea className="form-control" rows={2} value={deviationModal.disposition_comment}
                  onChange={(e) => setDeviationModal((prev) => ({ ...prev, disposition_comment: e.target.value }))}
                  placeholder="Rationale for this disposition..." />
              </div>
              <div className="form-group">
                <label className="form-label">Password (required to close)</label>
                <input type="password" className="form-control" value={deviationModal.password}
                  onChange={(e) => setDeviationModal((prev) => ({ ...prev, password: e.target.value }))} autoComplete="current-password" />
              </div>
            </>
          )}
        </Modal>
      )}
      <VersionHistoryModal
        isOpen={!!historyModal}
        onClose={() => setHistoryModal(null)}
        title={historyModal ? `Version History — Batch ${historyModal.batch_number || historyModal.result_id}` : ''}
        fetcher={() => resultsAPI.getRevisions(historyModal.result_id)}
      />
    </div>
  );
};

export default Results;
